/** @format */

import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { _$, Card, BreadcrumbItem, Icon, Button, Select, Option, Message } from 'kpc-react'
import { useParams } from 'react-router'
import API from 'src/api/ApiDashboard'
import useGlobalData from 'src/hooks/useGlobalData'
import MonitorCrumb from 'src/components/MonitorCrumb'
import QuickRangeDate, { QuickRangeDateMethod } from 'src/components/QuickRangeDate'
import MonitorView from 'src/containers/MonitorDashboard/Customboard/MonitorView'
import { useHistory } from 'react-router-dom'
import { request } from 'src/utils'
import {
  IMonitorGraphData,
  secondsToText,
  calcSelectInterval,
  delayToHours
} from 'src/containers/MonitorDashboard/Customboard/constants'
let timer = 0
const CustomboardPage: React.FC<{ className?: string }> = ({ className }) => {
  const { id: dashboardID } = useParams<{ name: string; id: string }>()
  const history = useHistory()
  const [dashboardName, setDashboardName] = useState<string>('')
  const [autoRefresh, setAutoRefresh] = useState<number>(0) // 自动刷新时间
  const [delayOption, setDelayOption] = useState<number[]>([])
  const [delay, setDelay] = useState<number>()
  const [secondsText, setSecondsText] = useState<string>('')
  const [monitorViewList, setMonitorViewList] = useState<IMonitorGraphData[]>([])
  const [dateTime, setDateTime] = useState<{ start: number; end: number }>({
    start: null,
    end: null
  })
  const [range, setRange] = useState<string>('')
  const autoIntervalRef = useRef<NodeJS.Timeout>() // 自动刷新Interval
  const quickDateRef = useRef<QuickRangeDateMethod>()
  // 产品列表
  const productList = useGlobalData('productList')
  // 开启自动刷新定时器
  const openRefresh = () => {
    autoIntervalRef?.current && clearInterval(autoIntervalRef.current)
    if (autoRefresh) {
      autoIntervalRef.current = setInterval(() => {
        timer = timer - 1
        setSecondsText(secondsToText(timer))
        if (timer <= 0) {
          quickDateRef.current.reloadDate()
        }
      }, 1000)
    }
  }

  // 如果手动选择日期禁用自动刷新
  useEffect(() => {
    if (!range) {
      autoIntervalRef?.current && clearInterval(autoIntervalRef.current)
      setAutoRefresh(0)
      resetTimer()
    }
  }, [range])

  // 初始化
  useEffect(() => {
    // getMetricStatisticsBatch()
    getDashboardChart()
    return () => {
      autoIntervalRef?.current && clearInterval(autoIntervalRef.current)
    }
  }, [])

  // 请求监控视图list
  const getDashboardChart = async () => {
    setMonitorViewList([])
    const { code, data } = await request(`${API.DescribeDashboard}`, {
      data: {
        DashboardId: dashboardID
      }
    })
    if (code === 'Success') {
      setMonitorViewList(data.dashboard.graphs)
      setDashboardName(data.dashboard.dashboardName)
    }
  }

  // 开启自动刷新
  useEffect(() => {
    openRefresh()
    resetTimer()
  }, [autoRefresh])

  // 重置倒计时
  const resetTimer = () => {
    setSecondsText(secondsToText(autoRefresh * 60))
    timer = autoRefresh * 60
  }

  // 日期变换的时候重新计算自动刷新时间/周期
  useEffect(() => {
    const { start, end } = dateTime
    if (monitorViewList?.length && start && end) {
      const delayArray = calcSelectInterval(start, end, monitorViewList)
      setDelay(delayArray[0])
      setDelayOption(delayArray)
      resetTimer()
    }
  }, [monitorViewList, dateTime?.start, dateTime?.end])

  return (
    <div className={className}>
      <MonitorCrumb breadArr={[{ title: _$('Dashboard'), route: '/monitor/dashboard/home' }]}>
        <BreadcrumbItem>{dashboardName}</BreadcrumbItem>
      </MonitorCrumb>
      <Card>
        <div className="header">
          <div className="left-header">
            <Button
              type="primary"
              onClick={event => {
                history.push(
                  // eslint-disable-next-line max-len
                  `/monitor/dashboard/detail/editGraph/${dashboardID}/${dashboardName}/craete`
                )
              }}
            >
              <Icon className="ion-plus-round" />
              {_$('添加监控视图')}
            </Button>
            <div className="auto-refresh">
              <span>{_$('自动刷新')}：</span>
              <Select
                disabled={!range}
                value={autoRefresh}
                onChange={value => {
                  setAutoRefresh(value)
                  Message.success(_$('自动更新设置成功'))
                }}
                width={100}
              >
                <Option value={0}>{_$('关闭')}</Option>
                <Option value={1}>{_$('1分钟')}</Option>
                <Option value={5}>{_$('5分钟')}</Option>
                <Option value={30}>{_$('30分钟')}</Option>
                <Option value={60}> {_$('60分钟')}</Option>
              </Select>
            </div>
            {secondsText !== '00:00' && (
              <div className="timer-box">
                <Icon className="ion-android-alarm-clock" />
                <span>{secondsText}</span>
              </div>
            )}
          </div>
          <div className="right-header">
            <QuickRangeDate
              ref={quickDateRef}
              realoadIcon={false}
              format="YYYY-MM-DD HH:mm"
              optionalDateRange={90}
              dateDiff={31}
              defautlValue={'近0.5小时'}
              quickRange={[
                '近5分钟',
                '近0.5小时',
                '近1小时',
                '近3小时',
                '近1天',
                '近3天',
                '近1周',
                '近30天'
              ]}
              // showShortcuts={false}
              changeValue={(date, range) => {
                setRange(range)
                setDateTime({
                  start: date[0],
                  end: date[1]
                })
              }}
            />
            <div className="delay-select">
              <span>{_$('周期')}：</span>
              <Select
                value={delay}
                onChange={value => {
                  setDelay(value)
                }}
                width={120}
              >
                {delayOption.length
                  ? delayOption.map(item => (
                      <Option key={item} value={item}>
                        {delayToHours(item)}
                      </Option>
                    ))
                  : null}
              </Select>
            </div>
          </div>
        </div>
        {productList?.length ? (
          <div className="body">
            {monitorViewList?.length && delayOption?.length
              ? // <ResponsiveGridLayout
                //   rowHeight={40}
                //   layouts={layouts}
                //   breakpoints={gridBreakpoints}
                //   margin={[10, 0]}
                //   cols={gridCols}
                //   isBounded={true}
                //   onLayoutChange={layout => {
                //     setCurrentLayout(layout)
                //     setLayouts({
                //       ...layouts,
                //       [layoutConfig.current.breakpoints]: layout
                //     })
                //   }}
                //   onBreakpointChange={(newBreakpoint, newCols) => {
                //     layoutConfig.current = {
                //       breakpoints: newBreakpoint,
                //       cols: newCols
                //     }
                //   }}
                // >
                monitorViewList.map((graph, index) => (
                  <div key={graph?.graphId}>
                    <MonitorView
                      productList={productList}
                      graph={graph}
                      dashboardName={dashboardName}
                      index={index}
                      start={dateTime.start}
                      end={dateTime.end}
                      delay={delay}
                      reload={getDashboardChart}
                    />
                  </div>
                ))
              : // </ResponsiveGridLayout>
                null}
          </div>
        ) : null}
      </Card>
    </div>
  )
}

export default styled(CustomboardPage)`
  .k-body {
    padding-bottom: 32px;
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      .left-header {
        padding-bottom: 16px;
        display: flex;
        align-items: center;
        & > * {
          margin-right: 8px;
        }
        .timer-box {
          display: flex;
          align-items: center;
          .k-icon {
            font-size: 18px;
            margin-right: 2px;
          }
        }
      }
      .right-header {
        padding-bottom: 16px;
        display: flex;
        align-items: center;
        & > * {
          margin-right: 8px;
        }
      }
    }
    .body {
      width: 100%;
      min-height: calc(100vh - 116px);
      padding-bottom: 16px;
    }
  }
`
